import type React from "react";
import {
  Children,
  createContext,
  createElement,
  isValidElement,
  useContext,
  useEffect,
  useState,
} from "react";

export type ViewMode = "preview" | "code" | "assets" | "state";

const useTabValue = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("preview");

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Shift + Option + Command + J (Mac) or Shift + Alt + Ctrl + J (Windows/Linux)
      if (
        e.shiftKey &&
        e.altKey && // Option on Mac maps to altKey
        (e.metaKey || e.ctrlKey) && // Command on Mac maps to metaKey
        e.code === "KeyJ"
      ) {
        e.preventDefault();
        setViewMode((prev) => (prev === "state" ? "preview" : "state"));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return { viewMode, setViewMode };
};

const TabContext = createContext<ReturnType<typeof useTabValue> | null>(null);

export const useTab = () => {
  const ctx = useContext(TabContext);
  if (!ctx) throw new Error("useTab must be used within TabProvider");
  return ctx;
};

export const TabProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useTabValue();
  return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
};

interface TabViewProps {
  name: ViewMode;
  component: React.ComponentType;
}

export const TabView: React.FC<TabViewProps> = () => null;

interface TabProps {
  children: React.ReactNode;
}

export const Tab: React.FC<TabProps> = ({ children }) => {
  const { viewMode } = useTab();

  let MatchedComponent: React.ComponentType | null = null;

  Children.forEach(children, (child) => {
    if (isValidElement<TabViewProps>(child) && child.props.name === viewMode) {
      MatchedComponent = child.props.component;
    }
  });

  return (
    <div className="flex-1 relative overflow-hidden">
      {MatchedComponent && createElement(MatchedComponent)}
    </div>
  );
};
